/** @format */

import { api } from "./client";

export type FeedbackCategory =
  | "unclear"
  | "incorrect"
  | "missing_step"
  | "other";

/** pending: Bedrock classification not finished yet; category may be null. */
export type FeedbackCategoryStatus = "pending" | "ready";

export type FeedbackStatus = "open" | "resolved";

export interface FeedbackItem {
  id: string;
  sopId: string;
  sopTitle?: string | null;
  versionId: string;
  /** When set, this feedback was submitted on an AI candidate rather than a published version. */
  candidateId?: string | null;
  versionNum?: number | null;
  versionTitle?: string | null;
  stepIndex: number;
  category: FeedbackCategory | null;
  /** Server-side Bedrock classification lifecycle; omit or "ready" for legacy rows. */
  categoryStatus?: FeedbackCategoryStatus;
  comment: string;
  status: FeedbackStatus;
  selectionFrom?: number | null;
  selectionTo?: number | null;
  selectedText?: string | null;
  /** True when the step content no longer contains the original selected text (overwritten). */
  selectionStale?: boolean | null;
  /** Set when this feedback was included in the current regeneration request. */
  selectedForRegenerationAt?: string | null;
  orgId: string | null;
  organization?: { id: string; name: string; slug?: string | null } | null;
  createdBy: string | null;
  createdByEmail?: string | null;
  createdByUser?: { email?: string | null; name?: string | null } | null;
  createdAt: string | null;
  updatedAt?: string | null;
}

/** Human-readable category while the server classifies (or after). */
export function formatFeedbackCategoryLabel(
  fb: Pick<FeedbackItem, "category" | "categoryStatus">,
): string {
  if (fb.categoryStatus === "pending" || fb.category == null) {
    return "Classifying…";
  }
  return fb.category.replace(/_/g, " ");
}

export interface CreateFeedbackPayload {
  sopTitle: string;
  versionId: string;
  /** When set, the feedback targets an AI candidate rather than a published version step. */
  candidateId?: string;
  stepIndex: number;
  comment: string;
  selectionFrom?: number;
  selectionTo?: number;
  selectedText?: string;
}

export interface FeedbackListParams {
  versionId?: string;
  /** When set, filters feedback to items submitted on a specific AI candidate. */
  candidateId?: string;
  stepIndex?: number;
  status?: FeedbackStatus;
  organizationId?: string;
  category?: string;
  sopId?: string;
  versionNum?: number;
  sopName?: string;
  owner?: string;
  page?: number;
  limit?: number;
}

export interface FeedbackListResponse {
  feedback: FeedbackItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasNext: boolean;
  };
}

export const feedbackService = {
  create: async (
    sopId: string,
    payload: CreateFeedbackPayload,
  ): Promise<{ feedbackId: string }> => {
    const response = await api.post(`/sops/${sopId}/feedback`, payload);
    return response.data;
  },

  list: async (
    sopId: string,
    params?: FeedbackListParams,
  ): Promise<FeedbackListResponse> => {
    const response = await api.get(`/sops/${sopId}/feedback`, { params });
    return response.data;
  },

  listAll: async (params?: {
    status?: string;
    category?: string;
    sopId?: string;
    versionId?: string;
    versionNum?: number;
    organizationId?: string;
    sopName?: string;
    owner?: string;
    page?: number;
    limit?: number;
  }): Promise<FeedbackListResponse> => {
    const response = await api.get("/feedback", { params });
    return response.data;
  },

  updateStatus: async (
    sopId: string,
    feedbackId: string,
    status: FeedbackStatus,
  ): Promise<FeedbackItem> => {
    const response = await api.patch(`/sops/${sopId}/feedback/${feedbackId}`, {
      status,
    });
    return response.data;
  },

  delete: async (sopId: string, feedbackId: string): Promise<void> => {
    await api.delete(`/sops/${sopId}/feedback/${feedbackId}`);
  },
};
